import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import { usePrincipalKey } from "@auth/sessionStore";
import { useFrameRefresh } from "@notifications/useFrameRefresh";
import { useQuery } from "@tanstack/react-query";

/*
 * Wire contract for Brian's scheduling read of a live call (BRI-151), served
 * by brian's `GET /phone-calls/{call_uuid}/schedule` (`state`) and built in
 * `backend/src/brian/modules/phone/schedule_picture.py`; change both together. Times are ISO 8601 instants; `date` fields are local clinic
 * dates (YYYY-MM-DD). Brian writes every sentence (`read`, `footer`); the
 * frontend formats only dates, times and labels.
 */

type CallScheduleStage = "heard" | "openings" | "proposed" | "conflict";

/**
 * One provider-day cell. `proposed`, `agreed` and `conflict` mark the start
 * the call is about; `moving` the visit being moved or cancelled; `window` a
 * day with openings in the asked range; `open`/`full` the day's own count.
 */
type CallScheduleTone =
  | "open"
  | "full"
  | "window"
  | "proposed"
  | "agreed"
  | "conflict"
  | "moving";

export interface CallScheduleProvider {
  id: string;
  name: string;
  shortName: string;
}

export interface CallScheduleCell {
  date: string;
  /** Bookable starts that day for the visit's duration. */
  open: number;
  tone: CallScheduleTone;
  /** The marked start (asked, taken, moving) when one falls on this day. */
  time?: string;
  label?: string;
}

/** Mon-Fri, plus Sat or Sun when a shown provider works it or a mark lands there. */
export interface CallScheduleWeek {
  start: string;
  days: string[];
  providers: (CallScheduleProvider & { cells: CallScheduleCell[] })[];
}

interface CallScheduleBooking {
  patient: string;
  visitType: string;
  start: string;
  provider: string;
}

export interface CallScheduleState {
  stage: CallScheduleStage;
  heard: {
    speaker: string;
    side: "caller" | "staff";
    at: string;
    text: string;
  };
  read: string;
  week: CallScheduleWeek;
  existing?: CallScheduleBooking & { visitUuid: string };
  proposal?: CallScheduleBooking;
  conflict?: { start: string; provider: string; holder: string };
  footer: string;
  /** When brian composed this state; a new value re-opens a dismissed bubble. */
  asOf: string;
}

const FRAME_TYPES = ["phone_call_schedule", "phone_call"];

/**
 * `stale` mirrors `phone_call_schedule_results.stale`; drop the intersection
 * once `pnpm gen:api` carries it.
 */
type CallScheduleOut = components["schemas"]["CallScheduleOut"] & {
  stale: boolean;
};

/** Brian's state for the call and how far to trust it. */
export interface CallScheduleRead {
  state: CallScheduleState;
  status: CallScheduleOut["status"];
  /** The last pass failed: the picture stays visible but is never actionable. */
  stale: boolean;
}

/**
 * Brian's scheduling read for the call: `GET /phone-calls/{uuid}/schedule`,
 * refreshed by `phone_call_schedule` frames and on reconnect. `state` is
 * stored as JSON by brian (`modules/phone/schedule_picture.py`) in exactly the
 * `CallScheduleState` shape.
 */
export const useCallSchedule = (callUuid: string): CallScheduleRead | null => {
  const principal = usePrincipalKey();
  const queryKey = ["call-schedule", principal, callUuid] as const;
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      request(() =>
        apiClient.GET("/phone-calls/{call_uuid}/schedule", {
          params: { path: { call_uuid: callUuid } },
          signal,
        }),
      ),
    enabled: principal !== "anon",
    staleTime: 5_000,
    retry: 1,
  });
  useFrameRefresh(FRAME_TYPES, callUuid, queryKey);
  const data = query.data as CallScheduleOut | undefined;
  if (!data?.state) return null;
  return {
    state: data.state as unknown as CallScheduleState,
    status: data.status,
    stale: data.stale,
  };
};
